import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Check, ChevronRight, FolderTree } from "lucide-react";
import AiHintsPanel from "./AiHintsPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const inputStyle = { background: "white", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "10px 12px", fontFamily: "Manrope", fontSize: "14px", outline: "none", width: "100%", color: "#0A0A0A" };
const labelStyle = { fontFamily: "Manrope", fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "4px", display: "block" };

function CategoryForm({ category, parents, onSave, onClose }) {
  const initial = category || { name: "", slug: "", parent_slug: "", sort_order: 0, is_active: true };
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const payload = { ...form, parent_slug: form.parent_slug || null };
      if (category?.id) {
        await axios.put(`${API}/categories/${category.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/categories`, payload, { withCredentials: true });
      }
      onSave();
    } catch (e) {
      alert(e?.response?.data?.detail || "Ошибка сохранения");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "20px", overflowY: "auto" }}>
      <div className="light-section" style={{ background: "white", borderRadius: "12px", width: "100%", maxWidth: "480px", padding: "24px", marginTop: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: "22px", fontWeight: 500, margin: 0 }}>{category ? "Редактировать категорию" : "Новая категория"}</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}><X size={20} /></button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={labelStyle}>Название</label>
            <input data-testid="cat-name-input" value={form.name} onChange={e => set("name", e.target.value)} style={inputStyle} placeholder="Шубы из норки" />
          </div>
          <div>
            <label style={labelStyle}>Родительская категория (необязательно)</label>
            <select data-testid="cat-parent-select" value={form.parent_slug || ""} onChange={e => set("parent_slug", e.target.value)} style={inputStyle}>
              <option value="">— Главная категория —</option>
              {parents.filter(p => !category || p.id !== category.id).map(p => (
                <option key={p.id} value={p.slug}>{p.name}</option>
              ))}
            </select>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
              Выберите, если это подкатегория (например, «Шубы из норки» внутри «Шубы»)
            </div>
          </div>
          <div>
            <label style={labelStyle}>Порядок отображения</label>
            <input type="number" value={form.sort_order} onChange={e => set("sort_order", parseInt(e.target.value) || 0)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontSize: "13px" }}>Отмена</button>
          <button data-testid="save-cat-btn" onClick={handleSave} disabled={loading || !form.name.trim()}
            style={{ flex: 2, background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "12px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Check size={15} />{loading ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminCategories() {
  const [tree, setTree] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultParent, setDefaultParent] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/categories`, { withCredentials: true });
      setTree(r.data || []);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const allFlat = tree.flatMap(t => [t, ...(t.subcategories || [])]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Удалить категорию «${name}»?`)) return;
    await axios.delete(`${API}/categories/${id}`, { withCredentials: true });
    load();
  };

  return (
    <div className="light-section" data-testid="admin-categories-page" style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <FolderTree size={22} style={{ color: "#C5A059" }} />
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: "26px", margin: 0, fontWeight: 500 }}>Категории товаров</h2>
        </div>
        <button data-testid="add-category-btn" onClick={() => { setEditing(null); setDefaultParent(""); setShowForm(true); }}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: "6px", padding: "10px 16px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}>
          <Plus size={14} />Добавить
        </button>
      </div>

      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}>
        {tree.length === 0 ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af", fontFamily: "Manrope", fontSize: "13px" }}>Пока нет категорий</div>
        ) : tree.map(top => (
          <div key={top.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
            <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", background: "#FDFBF7" }}>
              <ChevronRight size={14} style={{ color: "#C5A059" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "Cormorant Garamond", fontSize: "18px", fontWeight: 500 }}>{top.name}</div>
                <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "#9ca3af", letterSpacing: "0.08em" }}>/{top.slug}</div>
              </div>
              <button data-testid={`add-sub-${top.slug}`} onClick={() => { setEditing({ name: "", parent_slug: top.slug }); setShowForm(true); }}
                title="Добавить подкатегорию"
                style={{ background: "rgba(197,160,89,0.1)", border: "1px solid rgba(197,160,89,0.3)", borderRadius: "4px", padding: "6px 8px", cursor: "pointer", color: "#C5A059", display: "flex", alignItems: "center", gap: "3px", fontFamily: "Manrope", fontSize: "10px" }}>
                <Plus size={11} /> Подкат.
              </button>
              <button onClick={() => { setEditing(top); setShowForm(true); }} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "6px 8px", cursor: "pointer", color: "#374151" }}><Edit2 size={12} /></button>
              <button onClick={() => handleDelete(top.id, top.name)} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "6px 8px", cursor: "pointer", color: "#dc2626" }}><Trash2 size={12} /></button>
            </div>
            {top.subcategories && top.subcategories.length > 0 && (
              <div>
                {top.subcategories.map(sub => (
                  <div key={sub.id} style={{ padding: "10px 18px 10px 50px", display: "flex", alignItems: "center", gap: "10px", borderTop: "1px solid #f9fafb" }}>
                    <div style={{ flex: 1, fontFamily: "Manrope", fontSize: "13px", color: "#374151" }}>↳ {sub.name}</div>
                    <button onClick={() => { setEditing(sub); setShowForm(true); }} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "4px", padding: "5px 7px", cursor: "pointer", color: "#374151" }}><Edit2 size={11} /></button>
                    <button onClick={() => handleDelete(sub.id, sub.name)} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "4px", padding: "5px 7px", cursor: "pointer", color: "#dc2626" }}><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showForm && <CategoryForm category={editing} parents={tree} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}

      <AiHintsPanel />
    </div>
  );
}
